import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import shopifyBag from "../../../../assets/img/shopify-bag.svg";
import { ButtonT1 } from "../../../../components/atoms/Button/Button";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useGetShopifyInstallClaimQuery,
  useLinkShopifyInstallClaimMutation,
} from "../../../../store/apiSlice/shopifyApiSlice";
import { authUser, isAuthenticated } from "../../../../store/slice/authSlice";
import { EmptyStateCardDiv, EmptyStateWrapperDiv } from "../index.styles";

const RETURN_TO_KEY = "orderlink:returnTo";

const companyDisplayName = user => {
  if (!user) return "your company";
  if (typeof user.company === "string" && user.company.trim()) return user.company;
  if (user.company?.name) return user.company.name;
  return "your company";
};

/**
 * Public App Store install welcome: link an existing company or continue to signup.
 */
export const ShopifyWelcomePage = () => {
  const notify = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claim = searchParams.get("claim") || "";
  const isAuth = useSelector(isAuthenticated);
  const user = useSelector(authUser);
  const [linkError, setLinkError] = useState("");

  const {
    data: claimData,
    isLoading,
    isError,
    error,
  } = useGetShopifyInstallClaimQuery(claim, { skip: !claim });

  const [linkClaim, { isLoading: isLinking }] = useLinkShopifyInstallClaimMutation();

  const handleLogin = () => {
    sessionStorage.setItem(RETURN_TO_KEY, `/shopify/welcome?claim=${encodeURIComponent(claim)}`);
    navigate("/login");
  };

  const handleCreateAccount = () => {
    navigate(`/shopify/signup?claim=${encodeURIComponent(claim)}`);
  };

  const handleLink = async () => {
    setLinkError("");
    try {
      const result = await linkClaim(claim).unwrap();
      const path =
        result?.redirect_path ||
        (result?.config_id ? `/integration/shopify/${result.config_id}/settings` : null);
      navigate(path || "/integration/shopify");
    } catch (err) {
      const status = err?.status;
      const detail = err?.data?.detail || "Unable to link this Shopify store.";
      if (status === 403 || status === 409) {
        setLinkError(detail);
        notify({ type: "error", message: detail });
        return;
      }
      setLinkError(detail);
      notify({ type: "error", message: detail });
    }
  };

  if (!claim || isError) {
    const message =
      error?.data?.detail || "Open the app again from Shopify admin";
    return (
      <EmptyStateWrapperDiv>
        <EmptyStateCardDiv>
          <img className="shopifyBrandLogo" src={shopifyBag} alt="Shopify" />
          <div className="emptyHeading">Install link expired</div>
          <div className="emptyDescription">{message}</div>
        </EmptyStateCardDiv>
      </EmptyStateWrapperDiv>
    );
  }

  if (isLoading || !claimData) {
    return (
      <EmptyStateWrapperDiv>
        <EmptyStateCardDiv>
          <img className="shopifyBrandLogo" src={shopifyBag} alt="Shopify" />
          <div className="emptyHeading">Connecting Shopify…</div>
          <div className="emptyDescription">Please wait while we verify your install.</div>
        </EmptyStateCardDiv>
      </EmptyStateWrapperDiv>
    );
  }

  const shopName = claimData.shop_name || claimData.shop || "your Shopify store";
  const companyName = companyDisplayName(user);

  if (isAuth) {
    return (
      <EmptyStateWrapperDiv>
        <EmptyStateCardDiv>
          <img className="shopifyBrandLogo" src={shopifyBag} alt="Shopify" />
          <div className="emptyHeading">Link Shopify store</div>
          <div className="emptyDescription">
            Connect <b>{shopName}</b> to <b>{companyName}</b>.
          </div>
          {linkError ? (
            <div className="emptyDescription" style={{ color: "#c0392b" }}>
              {linkError}
            </div>
          ) : null}
          <ButtonT1
            title={`Link ${shopName} to ${companyName}`}
            onClickCallback={handleLink}
            loading={isLinking}
            disabled={isLinking}
          />
        </EmptyStateCardDiv>
      </EmptyStateWrapperDiv>
    );
  }

  return (
    <EmptyStateWrapperDiv>
      <EmptyStateCardDiv>
        <img className="shopifyBrandLogo" src={shopifyBag} alt="Shopify" />
        <div className="emptyHeading">Connect {shopName}</div>
        <div className="emptyDescription">
          {claimData.email_matches_user
            ? "An Orderlink account already exists for this store email. Log in to link the store."
            : "Log in to link this store to an existing company, or create a new Orderlink account."}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <ButtonT1 title="Log in" onClickCallback={handleLogin} />
          {claimData.self_signup_enabled ? (
            <ButtonT1
              title="Create account"
              $variant="secondary"
              onClickCallback={handleCreateAccount}
            />
          ) : null}
        </div>
      </EmptyStateCardDiv>
    </EmptyStateWrapperDiv>
  );
};
